import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  COLORS,
  ChartCard,
  DataTable,
  KpiCard,
  Notes,
  NotTrackedChips,
  formatCurrency,
  formatNumber,
  formatPercent,
  tooltipProps,
} from '../components/ChartKit';

const STATUS_LABELS = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Waiting for approval',
  CHANGES_REQUESTED: 'Changes requested',
  APPROVED: 'Approved',
  ACTIVE: 'Active',
  PAUSED: 'Paused',
  COMPLETED: 'Completed',
  ARCHIVED: 'Archived',
};
const statusLabel = (s) => STATUS_LABELS[s] || s || 'Unknown';

/** FR-M13-003 Campaign analytics */
const CampaignsTab = ({ data, dashboard }) => {
  const t = data.totals;
  const top = data.campaigns.slice(0, 8).map((c) => ({
    name: c.name.length > 22 ? `${c.name.slice(0, 21)}…` : c.name,
    fullName: c.name,
    Targeted: c.leadsTargeted,
    Qualified: c.qualifiedOrBetter,
    Meetings: c.meetings,
    Won: c.won,
  }));
  const statuses = data.statusCounts.map((s) => ({ ...s, label: statusLabel(s.label) }));

  return (
    <div className="analytics-tab-body">
      <div className="analytics-kpis">
        <KpiCard
          icon="fa-bullhorn"
          label="Campaigns"
          value={formatNumber(t.campaigns)}
          hint={dashboard.coverage.campaigns}
        />
        <KpiCard
          icon="fa-users"
          label="Leads targeted"
          value={formatNumber(t.leadsTargeted)}
          tone="violet"
        />
        <KpiCard
          icon="fa-calendar-check"
          label="Meetings from campaigns"
          value={formatNumber(t.meetings)}
          tone="cyan"
        />
        <KpiCard icon="fa-trophy" label="Deals won" value={formatNumber(t.won)} tone="green" />
        <KpiCard
          icon="fa-paper-plane"
          label="Emails sent"
          value={formatNumber(t.emailsSent)}
          hint="From outreach history in this period"
          tone="slate"
        />
      </div>

      <div className="analytics-grid">
        <ChartCard
          wide
          title="Campaign results"
          subtitle="Leads targeted and how far they progressed, for your largest campaigns"
          empty={!top.length}
          emptyText="No campaigns yet."
          table={{
            columns: ['Campaign', 'Targeted', 'Qualified or better', 'Meetings', 'Won'],
            rows: top.map((c) => [c.fullName, c.Targeted, c.Qualified, c.Meetings, c.Won]),
          }}
        >
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={top} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip
                {...tooltipProps}
                labelFormatter={(_, p) => p?.[0]?.payload?.fullName || ''}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Targeted" fill="#93c5fd" radius={[4, 4, 0, 0]} />
              <Bar
                dataKey="Qualified"
                name="Qualified or better"
                fill="#2563eb"
                radius={[4, 4, 0, 0]}
              />
              <Bar dataKey="Meetings" fill="#0891b2" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Won" fill="#16a34a" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Campaigns by status"
          empty={!statuses.length}
          table={{
            columns: ['Status', 'Campaigns'],
            rows: statuses.map((s) => [s.label, s.count]),
          }}
        >
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={statuses}
                dataKey="count"
                nameKey="label"
                innerRadius={50}
                outerRadius={85}
                paddingAngle={2}
              >
                {statuses.map((s, i) => (
                  <Cell key={s.label} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip {...tooltipProps} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          wide
          title="All campaigns"
          empty={!data.campaigns.length}
          emptyText="No campaigns yet."
        >
          <DataTable
            caption="All campaigns"
            rows={data.campaigns}
            columns={[
              { key: 'name', label: 'Campaign' },
              { key: 'status', label: 'Status', render: (c) => statusLabel(c.status) },
              {
                key: 'leadsTargeted',
                label: 'Leads',
                align: 'right',
                render: (c) => formatNumber(c.leadsTargeted),
              },
              {
                key: 'responses',
                label: 'Responses',
                align: 'right',
                render: (c) => formatNumber(c.responses),
              },
              {
                key: 'qualifiedOrBetter',
                label: 'Qualified+',
                align: 'right',
                render: (c) => formatNumber(c.qualifiedOrBetter),
              },
              {
                key: 'meetings',
                label: 'Meetings',
                align: 'right',
                render: (c) => formatNumber(c.meetings),
              },
              { key: 'won', label: 'Won', align: 'right', render: (c) => formatNumber(c.won) },
              {
                key: 'revenue',
                label: 'Revenue (USD)',
                align: 'right',
                render: (c) => formatCurrency(c.revenueUsd, 'USD'),
              },
              {
                key: 'conversionRate',
                label: 'Win rate',
                align: 'right',
                render: (c) => formatPercent(c.conversionRate),
              },
            ]}
          />
        </ChartCard>
      </div>

      <NotTrackedChips items={data.notTracked} />
      <Notes items={data.notes} />
    </div>
  );
};

export default CampaignsTab;
